<template>
    <div class="d-flex flex-column gap-3">
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <template v-else-if="project">
            <!-- Sticky Navigation -->
            <div class="col-12 sticky-top" style="z-index: 1000; top: 55px;">
                <div class="card border-0 shadow-sm rounded-4 mb-2">
                    <div class="card-body p-2">
                        <ul class="nav nav-pills nav-fill gap-1 small fw-bold flex-nowrap overflow-auto" style="scrollbar-width: none;">
                            <li class="nav-item">
                                <a class="nav-link bg-light text-dark cursor-pointer rounded-3" @click.prevent="scrollToSection('general-info')">General</a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link bg-light text-dark cursor-pointer rounded-3" @click.prevent="scrollToSection('project-actions')">Comments</a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link bg-light text-dark cursor-pointer rounded-3" @click.prevent="scrollToSection('upload-images')">Upload</a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link bg-light text-dark cursor-pointer rounded-3" @click.prevent="scrollToSection('project-history')">History</a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- Project Header -->
            <div class="card border-0 shadow-sm rounded-4 overflow-hidden mb-1" id="general-info">
                <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <h5 class="fw-bold mb-0">{{ project.name }}</h5>
                        <a href="/handyman/dashboard" class="text-theme cursor-pointer small text-decoration-none"><i class="bi bi-arrow-left pe-1"></i> Back</a>
                    </div>
                    <div class="text-muted small mb-2">
                        <i class="bi bi-calendar-event me-1"></i>
                        {{ formatDate(project.start_date) }} - {{ formatDate(project.end_date) }}
                    </div>
                    
                    <div class="d-flex align-items-start pt-2 border-top cursor-pointer" v-if="project.address" @click="openMap(project.address)">
                         <i class="bi bi-geo-alt text-primary mt-1 me-2 flex-shrink-0" style="font-size: 1.1em;"></i>
                        <div class="text-muted small">
                            <div class="fw-medium text-dark">{{ project.address?.address }}</div>
                            <div>{{ [project.address?.address_2, project.address?.city, project.address?.postal_code].filter(Boolean).join(', ') }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Customer Details -->
            <div class="card border-0 shadow-sm rounded-4 overflow-hidden mb-1">
                <div class="card-header bg-transparent border-0 pt-3 pb-0">
                    <h6 class="fw-bold mb-0">Customer Details</h6>
                </div>
                <div class="card-body p-3">
                    <div class="d-flex align-items-start mb-3">
                        <div class="rounded-circle bg-light d-flex align-items-center justify-content-center me-3 flex-shrink-0" style="width: 40px; height: 40px;">
                            <i class="bi bi-person text-secondary h5 mb-0"></i>
                        </div>
                        <div class="flex-grow-1">
                            <div class="fw-bold text-dark">{{ project.customer?.name || 'N/A' }}</div>
                            <div class="d-flex flex-column gap-1 mt-1">
                                <div class="d-flex align-items-center text-muted small" v-if="project.customer?.email">
                                    <i class="bi bi-envelope me-2"></i>
                                    <a :href="'mailto:' + project.customer.email" class="text-decoration-none text-muted">{{ project.customer.email }}</a>
                                </div>
                                <div class="d-flex align-items-center text-muted small" v-if="project.customer?.phone_number">
                                    <i class="bi bi-telephone me-2"></i>
                                    <a :href="'tel:' + project.customer.phone_number" class="text-decoration-none text-muted">{{ project.customer.phone_number }}</a>
                                </div>
                            </div>
                        </div>
                    </div>
                    

                </div>
            </div>

            <!-- Assigned Handymen -->
            <div class="card border-0 shadow-sm rounded-4 overflow-hidden mb-1" v-if="project.users && Object.keys(project.users).length">
                <div class="card-header bg-transparent border-0 pt-3 pb-0">
                    <h6 class="fw-bold mb-0">Assigned Handymen</h6>
                </div>
                <div class="card-body p-3">
                     <div v-for="(users, date) in project.users" :key="date" class="mb-3 last-mb-0">
                        <div class="d-flex align-items-center mb-2">
                            <i class="bi bi-calendar-day text-primary me-2"></i>
                            <span class="fw-bold small text-dark">{{ formatDate(date) }}</span>
                        </div>
                        <div class="d-flex flex-column gap-2 ps-3 border-start ms-2">
                            <div v-for="user in users" :key="user.id" class="d-flex align-items-center p-2 bg-light rounded-3 ms-2">
                                <div class="rounded-circle bg-white d-flex align-items-center justify-content-center me-2 border" style="width: 32px; height: 32px;">
                                     <span class="small fw-bold text-primary">{{ user.name.charAt(0).toUpperCase() }}</span>
                                </div>
                                <div class="flex-grow-1">
                                    <div class="fw-bold small">{{ user.name }}</div>
                                    <div v-if="user.email" class="text-muted" style="font-size: 11px;">
                                        <i class="bi bi-envelope me-1"></i>{{ user.email }}
                                    </div>
                                    <div class="d-flex gap-2" style="font-size: 11px;">
                                         <a v-if="user.phone_number" :href="'tel:' + user.phone_number" class="text-muted text-decoration-none">
                                            <i class="bi bi-telephone me-1"></i>{{ user.phone_number }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                     </div>
                </div>
            </div>



            <!-- Project Info -->
             <div class="card border-0 shadow-sm rounded-4 overflow-hidden mb-1">
                <div class="card-header bg-transparent border-0 pt-3 pb-0">
                    <h6 class="fw-bold mb-0">Project Info</h6>
                </div>
                <div class="card-body p-3">
                     <div class="row g-3">
                        <div class="col-6">
                            <div class="small text-muted">Start Date</div>
                            <div class="fw-medium">{{ formatDate(project.start_date) }}</div>
                        </div>
                        <div class="col-6">
                            <div class="small text-muted">End Date</div>
                            <div class="fw-medium">{{ formatDate(project.end_date) }}</div>
                        </div>
                         <div class="col-12" v-if="project.description">
                            <div class="small text-muted">Description</div>
                            <div class="fw-medium">{{ project.description }}</div>
                        </div>
                    </div>
                </div>
             </div>

            <!-- Project Actions / Comments -->
            <div class="card border-0 shadow-sm rounded-4 overflow-hidden mb-1" id="project-actions">
                <div class="card-header bg-transparent border-0 pt-3 pb-0">
                    <h6 class="fw-bold mb-0">Project Comments</h6>
                </div>
                <div class="card-body p-3">
                    <div class="form-group mb-2">
                        <textarea 
                            class="form-control rounded-3" 
                            rows="3" 
                            placeholder="Add a comment..."
                            v-model="comment"
                        ></textarea>
                    </div>
                    <button 
                        class="btn btn-theme w-100 rounded-3" 
                        @click="saveComment"
                        :disabled="!comment || updatingProject"
                    >
                        <span v-if="updatingProject" class="spinner-border spinner-border-sm me-1"></span>
                        Submit Comment
                    </button>
                </div>
            </div>

            <!-- Image Upload -->
            <div class="card border-0 shadow-sm rounded-4 overflow-hidden mb-1" id="upload-images">
                <div class="card-header bg-transparent border-0 pt-3 pb-0">
                    <h6 class="fw-bold mb-0">Upload Images</h6>
                </div>
                <div class="card-body p-3">
                    <ImageUploader 
                        :project-id="project.id" 
                        @uploads-completed="onUploadsCompleted" 
                    />
                </div>
            </div>

            <!-- Project History -->
            <div class="card border-0 shadow-sm rounded-4 overflow-hidden mb-1" id="project-history">
                <div class="card-header bg-transparent border-0 pt-3 pb-0">
                    <h6 class="fw-bold mb-0">Activity History</h6>
                </div>
                <div class="card-body p-3">
                    <div class="activity-timeline">
                        <template v-if="project.activities && project.activities.length">
                            <div v-for="activity in project.activities" :key="activity.id" class="activity-item d-flex mb-3">
                                <div class="activity-label text-muted text-end pe-3" style="width: 85px; min-width: 85px; font-size: 10px; line-height: 1.2;">
                                    <div class="fw-bold">{{ formatDate(activity.created_at, 'DD MMM, YYYY') }}</div>
                                    <div>{{ formatDate(activity.created_at, 'hh:mm A') }}</div>
                                </div>
                                <div class="activity-badge-container d-flex flex-column align-items-center me-3">
                                    <i class="bi bi-circle-fill text-theme small"></i>
                                    <div class="activity-line flex-grow-1 bg-light" style="width: 2px;"></div>
                                </div>
                                <div class="activity-content pb-3">
                                    <div class="fw-bold text-capitalize small mb-1">{{ activity.action.replace(/_/g, ' ') }}</div>
                                    <div class="text-muted small mb-1" v-if="activity.comment && activity.comment.trim() !== ''">{{ activity.comment }}</div>
                                    <div class="text-muted small fst-italic">By {{ activity.user.name }}</div>

                                    <!-- Render images if present in data -->
                                    <div v-if="activity.data && activity.data.images" class="mt-2 d-flex flex-wrap gap-2">
                                        <div v-for="(img, idx) in activity.data.images" :key="idx" class="history-image">
                                            <a :href="getImageUrl(img)" class="order-img-slides">
                                                <img :src="getImagePreview(img)" class="img-thumbnail rounded-3" style="width: 60px; height: 60px; object-fit: cover;">
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <p v-else class="text-muted small text-center py-3">No activity yet.</p>
                    </div>
                </div>
            </div>

        </template>
        
        <div v-else class="text-center py-5">
            <p class="text-muted">Project not found.</p>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import ImageUploader from './ImageUploader.vue';

export default {
    components: {
        ImageUploader
    },
    props: {
        projectId: {
            type: [String, Number],
            required: true
        }
    },
    data() {
        return {
            project: null,
            loading: true,
            comment: '',
            updatingProject: false
        }
    },
    mounted() {
        this.fetchProjectDetails();
    },
    methods: {
        async fetchProjectDetails() {
            this.loading = true;
            try {
                // If the page was loaded with data (passed via blade) we use that, 
                // but normally we might fetch via AJAX if it's strictly SPA-like within the blade.
                // Mimicking getOrderDetail: request same URL with AJAX.
                const response = await axios.get(window.location.href);
                if (response.data && response.data.project) {
                    this.project = response.data.project;
                    this.initSlider();
                }
            } catch (error) {
                console.error('Error fetching project details:', error);
                this.$notification('error', 'Failed to load project details');
            } finally {
                this.loading = false;
            }
        },
        formatDate(date, format = 'DD MMM, YYYY') {
            if (!date) return '-';
            return moment(date).format(format);
        },
        getGoogleMapsUrl(addressObj) {
            if (!addressObj) return '';
            const parts = [
                addressObj.address,
                addressObj.address_2,
                addressObj.city,
                addressObj.postal_code
            ].filter(Boolean);
            const query = parts.join(', ');
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
        },
        openMap(addressObj) {
            const url = this.getGoogleMapsUrl(addressObj);
            if (url) window.open(url, '_blank');
        },
        scrollToSection(sectionId) {
            const element = document.getElementById(sectionId);
            if (element) {
                const offset = 120; // Adjust for sticky header and nav
                const bodyRect = document.body.getBoundingClientRect().top;
                const elementRect = element.getBoundingClientRect().top;
                const elementPosition = elementRect - bodyRect;
                const offsetPosition = elementPosition - offset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        },
        async saveComment() {
            if (!this.comment || this.updatingProject) return;
            
            this.updatingProject = true;
            try {
                const response = await axios.post(`/handyman/projects/update/${this.projectId}`, {
                    comment: this.comment
                });
                
                if (response.data.status === 'success') {
                    this.$notification('success', 'Comment added successfully');
                    this.comment = '';
                    // Refresh project details to show new activity
                    this.fetchProjectDetails();
                }
            } catch (error) {
                console.error('Error saving comment:', error);
                this.$notification('error', 'Failed to add comment');
            } finally {
                this.updatingProject = false;
            }
        },
        onUploadsCompleted(uploadedFiles) {
            this.$notification('success', 'Images uploaded successfully');
            // Refresh activity history
            this.fetchProjectDetails();
        },
        getImageUrl(img) {
             if (img.stored_name) {
                 return this.$s3RootUrl + "/project/" + img.stored_name;
             }
             return img.url;
        },
        getImagePreview(img) {
            if (img.file_type === 'image') {
                return this.getImageUrl(img);
            } else if (img.file_type === 'video') {
                return '/images/svg/file-video.svg';
            } else if (img.file_type === 'pdf') {
                return '/images/svg/file-pdf.svg';
            }
            return '/images/svg/file-text.svg';
        },
        initSlider() {
            setTimeout(() => {
                if (typeof GLightbox !== 'undefined') {
                    GLightbox({
                        selector: '.order-img-slides',
                        touchNavigation: true,
                        loop: false,
                        autoplayVideos: true
                    });
                }
            }, 1000);
        }
    }
}
</script>

<style scoped>
.fw-medium {
    font-weight: 500;
}
.cursor-pointer {
    cursor: pointer;
}
.activity-line {
    width: 2px;
    height: 100%;
}
.activity-item:last-child .activity-line {
    display: none;
}
.activity-badge-container {
    width: 20px;
}
.activity-content {
    flex-grow: 1;
}
.activity-label {
    font-size: 10px;
}
.activity-content .timeline-title {
    font-size: 13px;
}
.activity-content .text-muted {
    font-size: 11px;
}
</style>
